import { Icon } from '@iconify/react';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, type AuthTokens, type Role, api, setSession } from './api';
import { AUTH_BUTTON, AUTH_CARD, AUTH_INPUT, AuthField, AuthShell } from './components/AuthShell';
import { PasswordInput } from './components/ui/Input';
import { type Lang, setLang, t } from './i18n';

/**
 * One sign-in screen for the whole family: husband (boss), wife (boss),
 * housekeeper (staff), nanny (staff). The form takes a single identifier
 * (email *or* username) plus password; the backend figures out the role.
 *
 * If the same username exists in multiple households, the server returns
 * AMBIGUOUS_USERNAME and we re-prompt with a household-code field.
 */
export function Signin() {
  const nav = useNavigate();
  // Where the route guard (or the 401 handler in api.ts) was trying to go
  // before it found no session. Honoured over the role's default landing
  // screen, so a push notification opened on an expired session still ends up
  // on the task it was about.
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [householdCode, setHouseholdCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [needsChange, setNeedsChange] = useState(false);
  const [role, setRole] = useState<Role>('staff');
  const [newPassword, setNewPassword] = useState('');

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      // Session is already set, so the call carries the fresh access token.
      // Server returns 204 and the token stays valid.
      await api('/v1/auth/staff/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: password, new_password: newPassword }),
      });
      nav(next ?? (role === 'owner' ? '/home' : '/today'));
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const body: Record<string, string> = {
        identifier: identifier.trim(),
        password,
      };
      if (needCode && householdCode.trim()) body.household_code = householdCode.trim();

      const res = await api<{
        tokens: AuthTokens;
        user: { preferred_lang: Lang; role: Role };
        must_change_password: boolean;
      }>('/v1/auth/signin', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setSession(res.tokens, res.user.role);
      setLang(res.user.preferred_lang);

      // A staff member's first password is one the owner typed for them and
      // handed over out loud or on paper. Until it's replaced, anyone who saw
      // it can sign in as them. The server flags this; we must act on it —
      // this screen used to read the flag and navigate straight past it.
      if (res.must_change_password) {
        setRole(res.user.role);
        setNeedsChange(true);
        return;
      }

      // Role-aware landing: bosses → dashboard, staff → today — unless they
      // were sent here from somewhere, in which case that somewhere wins.
      nav(next ?? (res.user.role === 'owner' ? '/home' : '/today'));
    } catch (e) {
      // Same shape for both: a username or an email that resolves to more
      // than one household needs a tiebreaker before the password can even
      // be checked. Without setting needCode here too, AMBIGUOUS_EMAIL told
      // a person to "add the household code" with no field to add it in.
      if (e instanceof ApiError && e.code === 'AMBIGUOUS_USERNAME') {
        setNeedCode(true);
        setErr(
          t('This username exists in more than one household. Add the household code to continue.'),
        );
      } else if (e instanceof ApiError && e.code === 'AMBIGUOUS_EMAIL') {
        setNeedCode(true);
        setErr(e.message);
      } else {
        setErr(e instanceof ApiError ? e.message : t('Failed'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (needsChange) {
    return (
      <AuthShell
        title={t('One more thing')}
        subtitle={t('Only you should know it — not the person who set up your account.')}
      >
        <form onSubmit={changePassword} className={`${AUTH_CARD} space-y-4`}>
          <AuthField label={t('New password')} htmlFor="signin-new-password">
            <input
              id="signin-new-password"
              className={AUTH_INPUT}
              type="password"
              placeholder={t('New password')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={10}
              autoComplete="new-password"
              // biome-ignore lint/a11y/noAutofocus: the reset step is a full-screen form whose only field is this one; focusing it saves a tap on mobile, which is the deliberate flow here
              autoFocus
              required
            />
          </AuthField>
          {err && (
            <div role="alert" className="text-sm font-semibold text-urgent">
              {err}
            </div>
          )}
          <button type="submit" className={AUTH_BUTTON} disabled={busy}>
            {busy ? '…' : t('Continue')}
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('Welcome back')}
      subtitle={t('Sign in to your household')}
      footer={
        <>
          <p className="text-[14px] font-medium tracking-tight text-muted-foreground">
            {t('New here?')}{' '}
            <Link
              to="/signup"
              className="font-bold text-primary transition-opacity active:opacity-70"
            >
              {t('Create your household')}
            </Link>
          </p>
          <div className="mt-14 flex items-center justify-center gap-2 text-[#C4C4C2]">
            <Icon icon="solar:lock-password-bold" className="shrink-0 text-sm" />
            <span className="max-w-[220px] text-[11px] font-bold uppercase tracking-[0.1em]">
              {t('Protected with two-factor authentication')}
            </span>
          </div>
        </>
      }
    >
      <form onSubmit={submit} className={`${AUTH_CARD} space-y-4`}>
        <AuthField label={t('Email or username')} htmlFor="signin-identifier">
          <input
            id="signin-identifier"
            className={AUTH_INPUT}
            dir="ltr"
            placeholder={t('Email or username')}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            // biome-ignore lint/a11y/noAutofocus: sign-in is a dedicated full-screen route and this is its first field; opening ready to type is the intended mobile-first flow
            autoFocus
            autoComplete="username"
            enterKeyHint="next"
            required
          />
        </AuthField>
        <AuthField label={t('Password')} htmlFor="signin-password">
          <PasswordInput
            id="signin-password"
            bare
            className={AUTH_INPUT}
            placeholder={t('Password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            enterKeyHint="go"
            required
          />
        </AuthField>
        {needCode && (
          <AuthField label={t('Household code')} htmlFor="signin-household-code">
            <input
              id="signin-household-code"
              className={AUTH_INPUT}
              dir="ltr"
              placeholder={t('Household code')}
              value={householdCode}
              onChange={(e) => setHouseholdCode(e.target.value.toUpperCase())}
              required
            />
          </AuthField>
        )}
        {err && (
          <div role="alert" className="text-sm font-semibold text-urgent">
            {err}
          </div>
        )}
        <button type="submit" className={`${AUTH_BUTTON} mt-2`} disabled={busy}>
          {busy ? '…' : t('Sign in')}
        </button>
        {/* The way back in. Until this link existed, an owner who forgot her
            password was locked out of her own household permanently — the
            reset endpoint was finished and nothing called it. */}
        <Link
          to="/forgot-password"
          className="block text-center text-[13px] font-bold text-muted-foreground transition-opacity active:opacity-70"
        >
          {t('Forgot password?')}
        </Link>
      </form>
    </AuthShell>
  );
}

/**
 * An in-app path and nothing else. `?next=` arrives from a link somebody may
 * have been sent, so anything that could leave the app — an absolute URL, a
 * protocol-relative `//evil.example` — is dropped rather than followed.
 */
function safeNext(raw: string | null): string | null {
  if (!raw) return null;
  return raw.startsWith('/') && !raw.startsWith('//') ? raw : null;
}
